import { useState, useEffect } from 'react';
import { getStatus, THRESHOLDS } from '../utils/riskCalculator';
import { useTranslation } from '../hooks/useTranslation';

/**
 * PortfolioForm component for user portfolio configuration
 */
export default function PortfolioForm({ onPortfolioChange, initialValues = {} }) {
  const { t } = useTranslation();
  const [exposurePercent, setExposurePercent] = useState(initialValues.exposurePercent || 5);
  const [vaultCount, setVaultCount] = useState(initialValues.vaultCount || 3);
  const [totalValue, setTotalValue] = useState(initialValues.totalValue || 100000);

  // Calculate exposure in USD
  const exposureUSD = (totalValue * exposurePercent) / 100;

  // Get status for exposure
  const exposureStatus = getStatus(exposurePercent, THRESHOLDS.portfolioExposure);

  // Update parent component when values change
  useEffect(() => {
    if (onPortfolioChange) {
      onPortfolioChange({
        exposurePercent,
        vaultCount,
        totalValue,
        exposureUSD
      });
    }
  }, [exposurePercent, vaultCount, totalValue, exposureUSD]); // Removed onPortfolioChange from deps

  // Status colors
  const statusColors = {
    green: 'text-success',
    yellow: 'text-warning',
    red: 'text-danger'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
        {t('portfolio.title')}
      </h2>

      {/* Total Portfolio Value */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('portfolio.totalValue')}
        </label>
        <input
          type="number"
          value={totalValue}
          onChange={(e) => setTotalValue(parseFloat(e.target.value) || 0)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          min="0"
          step="1000"
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('portfolio.totalValueDesc')}
        </p>
      </div>

      {/* Exposure Percentage */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('portfolio.exposureLabel')}
          </label>
          <span className={`text-lg font-bold ${statusColors[exposureStatus]}`}>
            {exposurePercent.toFixed(1)}%
          </span>
        </div>
        
        <input
          type="range"
          min="0"
          max="50"
          step="0.5"
          value={exposurePercent}
          onChange={(e) => setExposurePercent(parseFloat(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          style={{
            background: exposureStatus === 'red' 
              ? 'linear-gradient(to right, #EF4444 0%, #EF4444 100%)'
              : exposureStatus === 'yellow'
              ? 'linear-gradient(to right, #10B981 0%, #F59E0B 50%, #F59E0B 100%)'
              : 'linear-gradient(to right, #10B981 0%, #10B981 100%)'
          }}
        />
        
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
        </div>

        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{t('portfolio.exposureAmount')}</span>
            <span className="font-semibold text-gray-800 dark:text-white">
              ${exposureUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-2">
          {exposureStatus === 'green' && t('portfolio.exposureGreen')}
          {exposureStatus === 'yellow' && t('portfolio.exposureYellow')}
          {exposureStatus === 'red' && t('portfolio.exposureRed')}
        </p>
      </div>

      {/* Vault Diversification */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('portfolio.vaultDiversification')}
          </label>
          <span className={`text-lg font-bold ${vaultCount >= 3 ? 'text-success' : 'text-warning'}`}>
            {vaultCount} vaults
          </span>
        </div>
        
        <input
          type="range"
          min="1"
          max="10"
          step="1"
          value={vaultCount}
          onChange={(e) => setVaultCount(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
        />
        
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>1</span>
          <span>5</span>
          <span>10</span>
        </div>

        <p className="text-xs text-gray-500 mt-2">
          {vaultCount >= 3 ? t('portfolio.diversified') : t('portfolio.diversify')}
        </p>
      </div>

      {/* Recommendations */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
          💡 Recommendations
        </h3>
        <ul className="text-xs text-blue-800 dark:text-blue-400 space-y-1">
          <li>• {t('portfolio.rec1')}</li>
          <li>• {t('portfolio.rec2')}</li>
          <li>• {t('portfolio.rec3')}</li>
          <li>• {t('portfolio.rec4')}</li>
        </ul>
      </div>

      {/* Quick Actions */}
      <div className="mt-6">
        <div className="flex gap-2">
          <button
            onClick={() => {
              setExposurePercent(5);
              setVaultCount(3);
            }}
            className="flex-1 px-3 py-2 bg-success text-white rounded-lg hover:bg-success-dark transition-colors text-sm font-medium flex items-center justify-center gap-1"
            title={`${t('portfolio.conservative')} - 5% exposure, 3 vaults`}
          >
            <span className="text-xs">🛡️</span>
            <span className="truncate">{t('portfolio.conservative')}</span>
          </button>
          <button
            onClick={() => {
              setExposurePercent(10);
              setVaultCount(2);
            }}
            className="flex-1 px-3 py-2 bg-warning text-white rounded-lg hover:bg-warning-dark transition-colors text-sm font-medium flex items-center justify-center gap-1"
            title={`${t('portfolio.moderate')} - 10% exposure, 2 vaults`}
          >
            <span className="text-xs">⚖️</span>
            <span className="truncate">{t('portfolio.moderate')}</span>
          </button>
          <button
            onClick={() => {
              setExposurePercent(20);
              setVaultCount(1);
            }}
            className="flex-1 px-3 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark transition-colors text-sm font-medium flex items-center justify-center gap-1"
            title={`${t('portfolio.aggressive')} - 20% exposure, 1 vault`}
          >
            <span className="text-xs">⚡</span>
            <span className="truncate">{t('portfolio.aggressive')}</span>
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
          {t('portfolio.quickHelp')}
        </p>
      </div>
    </div>
  );
}

